import React, { useState, useEffect, useRef, useMemo } from "react";
import { Masonry } from "@mui/lab";
import ProjectCard from "./ProjectCard";
import { motion, AnimatePresence } from "framer-motion";
import { Cursor } from "react-creative-cursor";

function shuffleArray(array) {
  let tempArray = [...array];
  for (let i = tempArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [tempArray[i], tempArray[j]] = [tempArray[j], tempArray[i]];
  }
  return tempArray;
}

const ProjectGrid = ({ projects }) => {
  const [showCursor, setShowCursor] = useState(false);
  const gridRef = useRef();
  const cardRefs = useRef([]);

  // Shuffle Projects
  const randStickyProjects = useMemo(
    () => shuffleArray(projects.featuredProjects.edges),
    [projects.featuredProjects.edges],
  );
  const randProjects = useMemo(
    () => shuffleArray(projects.restOfProjects.edges),
    [projects.restOfProjects.edges],
  );

  const allProjects = randProjects;
  const [randomList, setList] = useState([...allProjects.slice(0, 15)]);
  const [loadMore, setLoadMore] = useState(true);
  const [hasMore, setHasMore] = useState(allProjects.length > 15);
  const loadRef = useRef();

  // Handle intersection with load more div
  const handleObserver = (entities) => {
    const target = entities[0];
    if (target.isIntersecting) {
      setLoadMore(true);
    }
  };

  //Initialize the intersection observer API
  useEffect(() => {
    var options = {
      root: null,
      rootMargin: "20px",
      threshold: 1.0,
    };
    const observer = new IntersectionObserver(handleObserver, options);
    if (loadRef.current) {
      observer.observe(loadRef.current);
    }
  }, []);

  // Handle loading more articles
  useEffect(() => {
    setList([...randomList]);
    if (loadMore && hasMore) {
      const currentLength = randomList.length;
      const isMore = currentLength < allProjects.length;
      const nextResults = isMore
        ? allProjects.slice(currentLength, currentLength + 15)
        : [];
      setList(randomList.concat(nextResults));
      setLoadMore(false);
    }
  }, [loadMore, hasMore]); //eslint-disable-line

  // Check if there is more
  useEffect(() => {
    const isMore = randomList.length < allProjects.length;
    setHasMore(isMore);
  }, [randomList]); //eslint-disable-line

  const [isAnyCardHovered, setIsAnyCardHovered] = useState(false);

  const handleMouseOver = () => {
    setIsAnyCardHovered(true);
    setShowCursor(true);
  };

  const handleMouseOut = () => {
    setIsAnyCardHovered(false);
    setShowCursor(false);
  };

  return (
    <>
      {showCursor ? <Cursor isGelly={true} cursorSize={20} /> : ""}
      <Masonry
        columns={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 4 }}
        spacing={{ xs: 2, sm: 1, md: 1, lg: 2, xl: 3 }}
        sx={{
          width: "auto",
          alignItems: "center",
          justifyItems: "center",
        }}
        id="projectsGrid"
        className={`${isAnyCardHovered ? "is-hovered" : ""}`}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        ref={gridRef}
      >
        <AnimatePresence>
          {randStickyProjects.map(({ node: project }, index) => {
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                exit={{ opacity: 0, y: -100 }}
              >
                <ProjectCard
                  key={index}
                  isHovered={isAnyCardHovered}
                  onMouseOver={handleMouseOver}
                  onMouseOut={handleMouseOut}
                  project={project}
                  ref={(element) => (cardRefs.current[index] = element)}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
        <AnimatePresence>
          {randomList.map(({ node: project }, index) => {
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }} // Stagger the delay
                exit={{ opacity: 0, y: -50 }} // Animate on exit if needed
              >
                <ProjectCard
                  key={index}
                  isHovered={isAnyCardHovered}
                  onMouseOver={handleMouseOver}
                  onMouseOut={handleMouseOut}
                  project={project}
                  ref={(element) =>
                    (cardRefs.current[index + randStickyProjects.length] =
                      element)
                  }
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </Masonry>
      <div className="grid-loadmore" ref={loadRef}>
        {hasMore ? (
          <p>Loading next batch of projects...</p>
        ) : (
          <p>All projects has been loaded.</p>
        )}
      </div>
    </>
  );
};

export default ProjectGrid;
